import { ReactElement, useState } from 'react';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';
import Button from '@components/Button';
import Icon from '@components/Icon';
import NetworkToNetworkInfo from '@components/NetworkToNetworkInfo';
import StackView from '@components/StackView';
import useGenericPopup from '@hooks/useGenericPopup';
import useGetReceiveAmount from '@hooks/useGetReceiveAmount';
import useSendTransactionContext from '@hooks/useSendTransactionContext';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import useWallets from '@hooks/useWallets';
import { InvalidInputError } from '@errors';
import FeeInfo from './FeeInfo';
import TransferCurrencyInfo, {
  TransferOperationEnum,
} from './TransferCurrencyInfo';
import { getDisplaySymbol } from '@lib/utils';

function ConfirmTransferDetailsView(): ReactElement {
  const [isLoading, setIsLoading] = useState(false);
  const { showPopup } = useGenericPopup();
  const { onNextStep, state } = useTransferAssetFlowContext();
  const { wallets } = useWallets();
  const { sendTransaction } = useSendTransactionContext();
  const getReceiveAmount = useGetReceiveAmount();
  const currencySymbol = state.tokenAsset?.symbol || '';

  const handleConfirmButtonClick = async () => {
    try {
      setIsLoading(true);
      await sendTransaction(wallets, state);
      onNextStep();
    } catch (error) {
      notify(error as NotifiableError);
      if (error instanceof InvalidInputError) {
        showPopup({
          header: 'Invalid input',
          body: 'Please verify and try again',
        });
        return;
      }
      showPopup({ header: 'Transaction Failed', body: 'Please try again' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StackView direction="vertical" spacing={2}>
      <div className="divide-y divide-white20 bg-white10 rounded-lg tablet:w-4/5 mobile:w-full mx-auto">
        <TransferCurrencyInfo
          amount={state.amount}
          currency={getDisplaySymbol(currencySymbol)}
          operation={TransferOperationEnum.Sending}
        />
        <NetworkToNetworkInfo from={state.fromNetwork} to={state.toNetwork} />
        {state.feeInfo && (
          <FeeInfo
            address={state.toAddress}
            addressNetwork={state.toNetwork}
            amount={
              state.feeInfo.isWaived
                ? 'Waived'
                : `${state.feeInfo.amount} ${getDisplaySymbol(
                    state.feeInfo.tokenAsset.symbol
                  )}`
            }
          />
        )}
        <TransferCurrencyInfo
          amount={getReceiveAmount(
            state.amount,
            state.feeInfo?.amount || '0',
            state.feeInfo?.tokenAsset?.decimals
          )}
          currency={getDisplaySymbol(currencySymbol)}
          operation={TransferOperationEnum.Receiving}
        />
      </div>
      <div className="tablet:w-4/5 mobile:w-full mx-auto">
        <Button
          className="w-full"
          disabled={isLoading}
          onClick={handleConfirmButtonClick}
        >
          {isLoading ? (
            <>
              <span>Confirming...&nbsp;</span>
              <Icon.Process className="text-iconSmall animate-spin" />
            </>
          ) : (
            <span>Confirm</span>
          )}
        </Button>
      </div>
    </StackView>
  );
}

export default ConfirmTransferDetailsView;
